import React, { useState } from 'react';
import {
  FormControl,
  Select,
  MenuItem,
  Box,
  Popover,
  Button,
  Typography,
  Chip,
  Stack,
  SelectChangeEvent
} from '@mui/material';
import {
  CalendarToday,
  DateRange as DateRangeIcon,
  Today,
  ViewWeek,
  CalendarMonth,
  CalendarViewMonth
} from '@mui/icons-material';
import { startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear, subDays, subMonths, subYears, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import DateInput from './DateInput';

export interface DateRangeValue {
  type: 'today' | 'week' | 'month' | 'year' | 'custom' | 'all';
  startDate: Date | null;
  endDate: Date | null;
  label: string;
}

// Helper function para converter tipo de período em DateRangeValue
export const getDateRangeFromType = (type: 'today' | 'week' | 'month' | 'year' | 'all'): DateRangeValue => {
  const now = new Date();

  switch (type) {
    case 'today':
      return {
        type: 'today',
        startDate: startOfDay(now),
        endDate: endOfDay(now),
        label: 'Hoje'
      };
    case 'week':
      return {
        type: 'week',
        startDate: startOfWeek(now, { locale: ptBR }),
        endDate: endOfWeek(now, { locale: ptBR }),
        label: 'Esta semana'
      };
    case 'month':
      return {
        type: 'month',
        startDate: startOfMonth(now),
        endDate: endOfMonth(now),
        label: 'Este mês'
      };
    case 'year':
      return {
        type: 'year',
        startDate: startOfYear(now),
        endDate: endOfYear(now),
        label: 'Este ano'
      };
    case 'all':
    default:
      return {
        type: 'all',
        startDate: null,
        endDate: null,
        label: 'Todos os períodos'
      };
  }
};

interface DateRangeFilterProps {
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
  showDateTypeSelector?: boolean; // Para relatórios: escolher entre createdAt ou completedAt; Para demandas: escolher entre createdAt ou estimatedCompletion
  dateType?: 'createdAt' | 'completedAt' | 'estimatedCompletion';
  onDateTypeChange?: (type: 'createdAt' | 'completedAt' | 'estimatedCompletion') => void;
  dateTypeOptions?: 'reports' | 'demands'; // 'reports' mostra createdAt/completedAt, 'demands' mostra createdAt/estimatedCompletion
  variant?: 'default' | 'mobile'; // Para usar estilos diferentes em mobile drawer
}

// eslint-disable-next-line max-lines-per-function -- débito pré-existente (F8), corrigir extraindo lógica
const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  value,
  onChange,
  showDateTypeSelector = false,
  dateType = 'createdAt',
  onDateTypeChange,
  dateTypeOptions = 'reports',
  variant = 'default'
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const selectRef = React.useRef<HTMLDivElement>(null);
  const isMobileVariant = variant === 'mobile';
  
  // Estilos baseados na variante
  // Quando usado dentro de um menu (popover), sempre usar estilo de texto normal (não branco)
  const selectSx = isMobileVariant ? {
    backgroundColor: 'background.paper',
    color: 'text.primary',
    '.MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
    '.MuiSvgIcon-root': { color: 'text.primary' }
  } : {
    backgroundColor: 'background.paper',
    color: 'text.primary',
    '.MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
    '.MuiSvgIcon-root': { color: 'text.primary' }
  };

  // Handler para abrir o popover de período customizado
  const handleOpenCustomPicker = () => {
    // Pré-preencher com valores atuais se existirem
    if (value.type === 'custom' && value.startDate && value.endDate) {
      setCustomStartDate(format(value.startDate, 'yyyy-MM-dd'));
      setCustomEndDate(format(value.endDate, 'yyyy-MM-dd'));
    }
    
    // Usar o ref do Select como âncora para o Popover
    if (selectRef.current) {
      setAnchorEl(selectRef.current);
    }
  };

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    const selectedType = event.target.value as DateRangeValue['type'];
    
    if (selectedType === 'custom') {
      handleOpenCustomPicker();
      return;
    }

    let newValue: DateRangeValue;
    const now = new Date();

    switch (selectedType) {
      case 'today':
        newValue = {
          type: 'today',
          startDate: startOfDay(now),
          endDate: endOfDay(now),
          label: 'Hoje'
        };
        break;
      case 'week':
        newValue = {
          type: 'week',
          startDate: startOfWeek(now, { locale: ptBR }),
          endDate: endOfWeek(now, { locale: ptBR }),
          label: 'Esta semana'
        };
        break;
      case 'month':
        newValue = {
          type: 'month',
          startDate: startOfMonth(now),
          endDate: endOfMonth(now),
          label: 'Este mês'
        };
        break;
      case 'year':
        newValue = {
          type: 'year',
          startDate: startOfYear(now),
          endDate: endOfYear(now),
          label: 'Este ano'
        };
        break;
      case 'all':
      default:
        newValue = {
          type: 'all',
          startDate: null,
          endDate: null,
          label: 'Todos os períodos'
        };
        break;
    }

    onChange(newValue);
  };

  const handleCustomDateApply = () => {
    if (customStartDate && customEndDate) {
      // Parse manual de yyyy-MM-dd para evitar interpretação em UTC,
      // que pode fazer a data voltar um dia dependendo do fuso horário.
      const [startYearStr, startMonthStr, startDayStr] = customStartDate.split('-');
      const [endYearStr, endMonthStr, endDayStr] = customEndDate.split('-');

      const startYear = Number(startYearStr);
      const startMonth = Number(startMonthStr);
      const startDay = Number(startDayStr);
      const endYear = Number(endYearStr);
      const endMonth = Number(endMonthStr);
      const endDay = Number(endDayStr);

      const startDate = startOfDay(new Date(startYear, startMonth - 1, startDay));
      const endDate = endOfDay(new Date(endYear, endMonth - 1, endDay));
      
      onChange({
        type: 'custom',
        startDate,
        endDate,
        label: `${format(startDate, 'dd/MM/yyyy', { locale: ptBR })} - ${format(endDate, 'dd/MM/yyyy', { locale: ptBR })}`
      });
      
      setAnchorEl(null);
      setCustomStartDate('');
      setCustomEndDate('');
    }
  };

  const handleCustomDateCancel = () => {
    setAnchorEl(null);
    setCustomStartDate('');
    setCustomEndDate('');
  };

  const renderQuickActions = () => {
    const now = new Date();
    const quickActions = [
      {
        label: 'Últimos 7 dias',
        startDate: startOfDay(subDays(now, 7)),
        endDate: endOfDay(now)
      },
      {
        label: 'Últimos 15 dias',
        startDate: startOfDay(subDays(now, 15)),
        endDate: endOfDay(now)
      },
      {
        label: 'Últimos 30 dias',
        startDate: startOfDay(subDays(now, 30)),
        endDate: endOfDay(now)
      },
      {
        label: 'Últimos 3 meses',
        startDate: startOfDay(subMonths(now, 3)),
        endDate: endOfDay(now)
      },
      {
        label: 'Últimos 6 meses',
        startDate: startOfDay(subMonths(now, 6)),
        endDate: endOfDay(now)
      },
      {
        label: 'Último ano',
        startDate: startOfDay(subYears(now, 1)),
        endDate: endOfDay(now)
      }
    ];

    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" sx={{ mb: 1, display: 'block', color: 'text.secondary' }}>
          Atalhos:
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
          {quickActions.map((action) => (
            <Chip
              key={action.label}
              label={action.label}
              size="small"
              onClick={() => {
                onChange({
                  type: 'custom',
                  startDate: action.startDate,
                  endDate: action.endDate,
                  label: action.label
                });
                setAnchorEl(null);
              }}
              sx={{ cursor: 'pointer' }}
            />
          ))}
        </Stack>
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'stretch', md: 'center' }, gap: 2, width: { xs: '100%', md: 'auto' } }}>
      {showDateTypeSelector && onDateTypeChange && (
        <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 180 }, width: { xs: '100%', md: 'auto' } }}>
          <Select
            value={dateType}
            onChange={(e) => {
              const selectedValue = e.target.value as 'createdAt' | 'completedAt' | 'estimatedCompletion';
              if (onDateTypeChange) {
                onDateTypeChange(selectedValue);
              }
            }}
            displayEmpty
            sx={selectSx}
          >
            {dateTypeOptions === 'reports' ? (
              <>
                <MenuItem value="createdAt">Data de Abertura</MenuItem>
                <MenuItem value="completedAt">Data de Conclusão</MenuItem>
              </>
            ) : (
              <>
                <MenuItem value="createdAt">Data de Abertura</MenuItem>
                <MenuItem value="estimatedCompletion">Prazo Estimado</MenuItem>
              </>
            )}
          </Select>
        </FormControl>
      )}

      <FormControl ref={selectRef} size="small" fullWidth sx={{ minWidth: { xs: '100%', md: 220 } }}>
        <Select
          value={value.type}
          onChange={handleSelectChange}
          displayEmpty
          startAdornment={<CalendarToday sx={{ mr: 1, fontSize: 20 }} />}
          renderValue={(selected) => {
            if (!selected || selected === 'all') {
              return <em>Todos os períodos</em>;
            }
            return <span>{value.label}</span>;
          }}
          sx={selectSx}
        >
          <MenuItem value="all">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarViewMonth />
              <span>Todos os períodos</span>
            </Box>
          </MenuItem>
          <MenuItem value="today">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Today />
              <span>Hoje</span>
            </Box>
          </MenuItem>
          <MenuItem value="week">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ViewWeek />
              <span>Esta semana</span>
            </Box>
          </MenuItem>
          <MenuItem value="month">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarMonth />
              <span>Este mês</span>
            </Box>
          </MenuItem>
          <MenuItem value="year">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarToday />
              <span>Este ano</span>
            </Box>
          </MenuItem>
          <MenuItem 
            value="custom"
            onClick={(_e) => {
              // Forçar abertura do popover mesmo se já estiver selecionado
              handleOpenCustomPicker();
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DateRangeIcon />
              <span>Período customizado</span>
            </Box>
          </MenuItem>
        </Select>
      </FormControl>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleCustomDateCancel}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: { xs: 2, sm: 3 }, minWidth: { xs: '100%', sm: 400 }, maxWidth: { xs: '100vw', sm: 400 } }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Selecionar Período Customizado
          </Typography>
          
          {renderQuickActions()}
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <DateInput
              label="Data Inicial"
              value={customStartDate}
              onChange={(value) => setCustomStartDate(value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <DateInput
              label="Data Final"
              value={customEndDate}
              onChange={(value) => setCustomEndDate(value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button onClick={handleCustomDateCancel}>
                Cancelar
              </Button>
              <Button
                variant="contained"
                onClick={handleCustomDateApply}
                disabled={!customStartDate || !customEndDate}
              >
                Aplicar
              </Button>
            </Box>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default DateRangeFilter;

